import { useEffect, useState } from 'react';

import { getPopular } from '../utils';
import { cars } from '../constants';

import { useSearchParams, useNavigate, useLocation, useParams } from 'react-router-dom';

/**
 * Custom hook for filtering, sorting, and paginating cars.
 */
export const useCarFilter = () => {

  const location = useLocation();
  const navigate = useNavigate();
  const { search } = useParams();
  const [searchParams] = useSearchParams();

  // State variables
  const [matchingCars, setMatchingCars] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOption, setSortOption] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');

  const [filters, setFilters] = useState({
    brands: [],
    types: [],
    transmissions: [],
    fuels: [],
    priceRange: [0, 400]
  });

  const itemsPerPage = location.pathname.toLowerCase().includes('list') ? 6 : 8;

  // Synchronize search keyword with URL params
  useEffect(() => {
    setSearchKeyword(search || '');
  }, [search]);

  // Update filters from URL params when component mounts
  useEffect(() => {
    const params = extractSearchParams(searchParams);
    const newFilters = {
      brands: params.brand ? [params.brand.toLowerCase()] : [],
      types: params.type ? [params.type.toLowerCase().replace(' ', '-')] : [],
      transmissions: params.transmission ? [params.transmission.toLowerCase()] : [],
      fuels: params.fuelType ? [params.fuelType.toLowerCase()] : [],
      priceRange: params.maxPrice ? [0, parseInt(params.maxPrice)] : [0, 400]
    };
    setFilters(newFilters);
  }, []);

  // Filter and sort cars whenever filters, search keyword, or sort option changes
  useEffect(() => {
    let filteredCars = handleFilterCars(cars, filters);

    // Apply header search keyword filter if exists
    if (searchKeyword) {
      filteredCars = handleHeaderSearch(searchKeyword, filteredCars);
    }

    // Sort filtered cars
    filteredCars = handleSortCars(filteredCars, sortOption);

    // Update matching cars and reset to the first page
    setMatchingCars(filteredCars);
    setCurrentPage(1);
  }, [filters, searchKeyword, sortOption]);

  // Main filter function for CarSearch component
  const handleFilterCars = (cars, filters) => {
    return cars.filter((car) => {
      const priceInRange = car.price >= filters.priceRange[0] && car.price <= filters.priceRange[1];
      
      const brandMatch = filters.brands.length === 0 || 
        filters.brands.includes(car.details.brand.toLowerCase());
      
      const typeMatch = filters.types.length === 0 || 
        filters.types.some(type => 
          car.details.type.toLowerCase().includes(type.replace('-', ' ')));
      
      const transmissionMatch = filters.transmissions.length === 0 || 
        filters.transmissions.some(trans => 
          car.details.transmission.map(t => t.toLowerCase()).includes(trans));
      
      const fuelMatch = filters.fuels.length === 0 || 
        filters.fuels.some(fuel => 
          car.details.fuelType.map(f => f.toLowerCase()).includes(fuel));
      
      return priceInRange && brandMatch && typeMatch && transmissionMatch && fuelMatch;
    });
  };

  // Handles sorting of cars based on the selected option.
  const handleSortCars = (cars, sortOption) => {
    switch (sortOption) {
      case 'featured':
        return getPopular(cars);
      case 'latest':
        return [...cars].sort((a, b) => b.details.year - a.details.year);
      case 'lowPrice':
        return [...cars].sort((a, b) => a.price - b.price);
      case 'highPrice':
        return [...cars].sort((a, b) => b.price - a.price);
      default:
        return cars;
    }
  };

  // Filters cars based on a search keyword (header search).
  const handleHeaderSearch = (searchTerm, carsToFilter = cars) => {
    const searchLower = searchTerm.toLowerCase();
    return carsToFilter.filter((item) => (
      String(item.details.year)?.toLowerCase().includes(searchLower) || 
      item.details.brand?.toLowerCase().includes(searchLower) ||
      item.name?.toLowerCase().includes(searchLower) ||
      item.details.type?.toLowerCase().includes(searchLower) ||
      item.details.fuelType?.some(f => f.toLowerCase().includes(searchLower)) ||
      item.details.transmission?.some(t => t.toLowerCase().includes(searchLower))
    ));
  };

  // Update URL with current filters
  const updateUrlWithFilters = (filters) => {
    const params = new URLSearchParams();
    
    if (filters.brands.length > 0) {
      params.set('brand', filters.brands[0]);
    }
    
    if (filters.types.length > 0) {
      params.set('type', filters.types[0].replace('-', ' '));
    }
    
    if (filters.fuels.length > 0) {
      params.set('fuelType', filters.fuels[0]);
    }
    
    if (filters.transmissions.length > 0) {
      params.set('transmission', filters.transmissions[0]);
    }
    
    if (filters.priceRange[1] < 200) {
      params.set('maxPrice', filters.priceRange[1].toString());
    }
    

  };

  // Handle filter changes from CarSearch component
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    updateUrlWithFilters(newFilters);
  };

  // Handles header search form submission and updates the URL.
  const handleHeaderSearchSubmit = (selectedSearchQuery, type) => {
    const encodedQuery = encodeURIComponent(selectedSearchQuery);
    navigate(`/Cars/search/${encodedQuery}`);
  };

  // Function to handle sorting option change
  const handleSort = (selectedOption) => {
    setSortOption(selectedOption ? selectedOption.value : '');
    setCurrentPage(1);
  };

  // Pagination calculations
  const displayCars = matchingCars.length > 0 ? matchingCars : cars;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayedCars = displayCars.slice(startIndex, endIndex);
  const totalPages = Math.ceil(displayCars.length / itemsPerPage);

  // Function to handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return {
    searchKeyword,
    matchingCars,
    sortOption,
    currentPage,
    displayedCars,
    filters,
    handleFilterChange,
    handleSort,
    totalPages,
    handlePageChange,
    handleHeaderSearchSubmit
  };
};

// ================================
// Extract Dynamic Counts for Filtering
// ================================

const carBrandCounts = {};
const carModelsByBrand = {};
const carTypeCounts = {};
const carFuelCounts = {};
const carTransmissionCounts = {};

// Iterate through the cars array to populate counts
cars.forEach((car) => {
  const brand = car.details.brand.toLowerCase();
  const model = car.name.toLowerCase();
  const type = car.details.type.toLowerCase();
  
  // Handle fuel types (array)
  car.details.fuelType.forEach(fuel => {
    const fuelLower = fuel.toLowerCase();
    carFuelCounts[fuelLower] = (carFuelCounts[fuelLower] || 0) + 1;
  });
  
  // Handle transmissions (array)
  car.details.transmission.forEach(trans => {
    const transLower = trans.toLowerCase();
    carTransmissionCounts[transLower] = (carTransmissionCounts[transLower] || 0) + 1;
  });

  carBrandCounts[brand] = (carBrandCounts[brand] || 0) + 1;
  carTypeCounts[type] = (carTypeCounts[type] || 0) + 1;

  if (!carModelsByBrand[brand]) {
    carModelsByBrand[brand] = new Set();
  }
  carModelsByBrand[brand].add(model);
});

// ================================
// Generate Dropdown Options
// ================================

// Generate car brand options dynamically
export const carBrands = [
  { value: '', label: 'Car Brand' },
  ...Object.keys(carBrandCounts).map((brand) => ({
    value: brand,
    label: brand.charAt(0).toUpperCase() + brand.slice(1),
  })),
];

// Generate car type options dynamically
export const carTypes = [
  { value: '', label: 'Car Type' },
  ...Object.keys(carTypeCounts).map((type) => ({
    value: type,
    label: type.charAt(0).toUpperCase() + type.slice(1),
  })),
];

// Generate fuel type options dynamically
export const carFuelTypes = [
  { value: '', label: 'Fuel Type' },
  ...Object.keys(carFuelCounts).map((fuel) => ({
    value: fuel,
    label: fuel.charAt(0).toUpperCase() + fuel.slice(1),
  })),
];

// Generate transmission options dynamically
export const carTransmissions = [
  { value: '', label: 'Transmission' },
  ...Object.keys(carTransmissionCounts).map((transmission) => ({
    value: transmission,
    label: transmission.charAt(0).toUpperCase() + transmission.slice(1),
  })),
];

// ================================
// Utility Functions
// ================================

export const extractSearchParams = (searchParams) => {
  return {
    brand: searchParams.get('brand') || '',
    model: searchParams.get('model') || '',
    type: searchParams.get('type') || '',
    fuelType: searchParams.get('fuelType') || '',
    transmission: searchParams.get('transmission') || '',
    maxPrice: searchParams.get('maxPrice') || '',
  };
};

// Function to get car details by their IDs
export const getCarsByIds = (featuredCarIdsIds) => {
  // Map through each car ID and find the corresponding car object from the cars array
  return featuredCarIdsIds.map(id => cars.find(car => car.id === id));
};